import { useGetMerchants, type Merchant } from '../api/useGetMerchants';
import { DataTable, type Column } from './DataTable';
import { Pagination } from './Pagination';
import { SearchInput } from './SearchInput';
import { AccountStatusBadge } from './AccountStatusBadge';
import { AccountStatusAction } from './AccountStatusAction';
import { useUrlSearch } from '../hooks/useUrlSearch';
import { useFittedPageSize } from '../hooks/useFittedPageSize';
import { formatDate } from '../lib/date';

const columns: Column<Merchant>[] = [
  { key: 'uid', header: 'UID', className: 'font-mono text-xs text-gray-400', cell: (m) => `${m.uid.slice(0, 8)}…`, copyValue: (m) => m.uid },
  { key: 'name', header: 'Name', className: 'text-gray-900', cell: (m) => m.name, copyValue: (m) => m.name },
  { key: 'email', header: 'Email', cell: (m) => m.email, copyValue: (m) => m.email },
  { key: 'phone', header: 'Phone', cell: (m) => m.phone, copyValue: (m) => m.phone },
  { key: 'created', header: 'Created', className: 'text-gray-500', cell: (m) => formatDate(m.created_at) },
  { key: 'status', header: 'Status', cell: (m) => <AccountStatusBadge status={m.status} remarks={m.remarks} /> },
  {
    key: 'actions',
    header: '',
    cell: (m) => <AccountStatusAction url={`/v1/ops/merchants/${m.uid}/status`} cacheKey="merchants" status={m.status} name={m.name} />,
  },
];

export const MerchantsTable = () => {
  const { query, page, search, setSearch, goToPage } = useUrlSearch();
  const { tableRef, pageSize } = useFittedPageSize();
  const { data, isLoading, isError } = useGetMerchants({ page, limit: pageSize, q: query || undefined });

  const merchants = data?.payload?.merchants ?? [];
  const total = data?.payload?.total ?? 0;
  const showPagination = !isLoading && !isError && merchants.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <SearchInput value={search} onChange={setSearch} placeholder="Search by name or email" label="Search vendors" />
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rows={merchants}
          rowKey={(m) => m.uid}
          isLoading={isLoading}
          isError={isError}
          errorLabel="Failed to load vendors."
          emptyLabel="No vendors found."
        />
      </div>
      {showPagination && (
        <Pagination page={page} pageSize={pageSize} total={total} onPageChange={goToPage} />
      )}
    </div>
  );
};
